import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const EducationConsulting = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const helpItems = [
    "Understanding and navigating the IEP or 504 plan process",
    "Advocating for appropriate accommodations and services",
    "Communicating effectively with school teams and administrators",
    "Identifying learning, behavioral, or emotional needs in the school setting",
    "Preparing for school evaluations or eligibility meetings",
    "Supporting transitions between schools or grade levels",
    "Navigating behavioral intervention plans (BIPs)",
    "Bridging the gap between clinical recommendations and school implementation"
  ];

  const fitItems = [
    "You feel overwhelmed or confused by the IEP or 504 process",
    "You want an advocate who understands both the clinical and school side",
    "Your child's needs aren't being fully met in the classroom",
    "You've been told your child doesn't qualify for services — but something still feels off",
    "You need help translating clinical evaluations into school-based support",
    "You want a professional who speaks both languages — therapy and education"
  ];

  return (
    <div className="therapy-site" data-testid="education-consulting-page">
      <Helmet>
        <title>Education Consulting & School Support | Inspire Therapy Collective - Orlando</title>
        <meta name="description" content="Expert education consulting and school support services including IEP advocacy, 504 plans, and school system navigation in Lake Nona, Orlando and throughout Florida and Virginia." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div 
          className="service-hero-overlay" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1745273619794-efe47c73826c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxzY2hvb2wlMjBjbGFzc3Jvb20lMjBlZHVjYXRpb24lMjBzdXBwb3J0JTIwd2FybXxlbnwwfHx8fDE3NzIwNTcwOTR8MA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="education-page-title">Education Consulting & School Support</h1>
          <p className="service-tagline">
            Expert guidance navigating IEPs, 504 plans, and school systems — from someone who's been on the inside.
          </p>
          <span className="service-location-tag">Lake Nona, Orlando FL &bull; Virtual consultations available</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              Navigating the school system on behalf of your child can feel overwhelming, confusing, and sometimes even adversarial. IEP meetings, 504 accommodations, eligibility criteria, behavioral plans — it's a lot, especially when you're not sure what your child is entitled to or how to ask for it.
            </p>
            <p>
              With over two decades of experience in school settings, I've sat on the other side of that table hundreds of times. I understand how school systems work from the inside — the language, the processes, the politics, and the gaps. Now I use that knowledge to empower parents and advocate for children who need more support than they're getting.
            </p>
            <p>
              Whether you need help preparing for an IEP meeting, understanding evaluation results, or pushing for accommodations that actually match your child's needs — I'm here to guide you through it with clarity and confidence.
            </p>
          </div>
        </div>
      </section>

      {/* What I Help With */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="education-help-heading">What I Help With</h2>
          <div className="service-help-grid">
            {helpItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`helpIconEdu${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#helpIconEdu${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#helpIconEdu${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">Why Work with Me</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerEdu" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerEdu)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerEdu)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              Most therapists understand diagnoses. Most teachers understand classrooms. I understand both — and how they connect. My background as a school social worker and licensed clinical practitioner gives me a rare perspective that bridges the gap between clinical recommendations and school-based implementation.
            </p>
            <p>
              I help parents feel prepared, informed, and empowered. I'll walk you through what to expect, what to ask for, and how to ensure your child's needs are being met — not just on paper, but in practice.
            </p>
            <p>
              Education consulting can be offered alongside therapy or as a standalone service. If your child is already in therapy with me, I can seamlessly integrate school advocacy into our work. If you just need guidance on the school side, that's perfectly fine too.
            </p>
          </div>
        </div>
      </section>

      {/* Good Fit */}
      <section className="service-fit-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">This Might Be a Good Fit If...</h2>
          <div className="service-fit-list">
            {fitItems.map((item, index) => (
              <div key={index} className="service-fit-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="fit-bullet" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Let's Advocate Together</h2>
            <p>
              Your child deserves the right support in every setting. If you're feeling stuck, confused, or unheard by the school system — let's change that.
            </p>
            <div className="hero-cta-group" style={{ justifyContent: 'center' }}>
              <button onClick={goToContact} className="btn btn-primary" data-testid="education-cta-primary">
                Book a Free 15-Min Consult
              </button>
              <button onClick={() => navigate('/', { state: { scrollTo: 'insurance' } })} className="btn btn-secondary-outline" data-testid="education-cta-secondary">
                Check Insurance & Book
              </button>
            </div>
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Lake Nona, Orlando FL &bull; Virtual consultations available</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="explore-all-services-link">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EducationConsulting;
